import React, { useState } from "react";
import { api } from "../lib/api";
import { Mail, Loader2, Check, Printer } from "lucide-react";
import { toast } from "sonner";

/**
 * Email opt-in for the "Printable Animal-Safety Recommendations" lead magnet.
 * Used on Home and in the Footer.
 *
 * Props:
 *   variant: "card" (default, dark hero block) | "inline" (slim, for footer)
 *   source:  string label saved on the subscriber doc (e.g. "home_hero")
 */
export default function SubscribeForm({ variant = "card", source = "printable_recommendations" }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/subscribers", { email: email.trim().toLowerCase(), source });
      setDone(true);
      if (r.data?.already_subscribed) {
        toast.success("You're already on the list — we've got you.");
      } else {
        toast.success("You're in. Check your inbox for the printable.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Signup failed. Try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (variant === "inline") {
    return (
      <form onSubmit={submit} className="flex items-stretch gap-0 border border-zinc-700 bg-zinc-900" data-testid="subscribe-form-inline">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="flex-1 bg-transparent px-3 py-2 text-sm text-white placeholder:text-zinc-500 outline-none"
          required
          data-testid="subscribe-email-input"
          disabled={done}
        />
        <button
          type="submit"
          disabled={submitting || done}
          className="bg-red-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-red-700 disabled:opacity-60 inline-flex items-center gap-1.5"
          data-testid="subscribe-submit"
        >
          {done ? <Check size={12} /> : submitting ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
          {done ? "Subscribed" : "Get it"}
        </button>
      </form>
    );
  }

  return (
    <div
      className="border-2 border-zinc-900 bg-amber-50 p-6 sm:p-8"
      data-testid="subscribe-form-card"
    >
      <div className="flex items-start gap-4">
        <div className="bg-zinc-900 text-white p-3 flex-shrink-0">
          <Printer size={22} />
        </div>
        <div className="flex-1">
          <div className="label-eyebrow text-red-600">Free · Printable</div>
          <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tighter mt-1 leading-tight">
            Animal-Safety Recommendations
          </h3>
          <p className="text-sm text-zinc-700 mt-2 max-w-md">
            A one-page printable for shelters, fosters and adopters: red flags to
            screen for, what to ask, and how to use the registry before placing
            an animal. Drop your email and we&apos;ll send it.
          </p>

          {done ? (
            <div className="mt-4 flex items-center gap-2 text-sm font-bold text-emerald-700" data-testid="subscribe-success">
              <Check size={16} /> You&apos;re in — check your inbox.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-0" data-testid="subscribe-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                className="flex-1 border-2 border-zinc-900 bg-white px-4 py-3 text-base outline-none placeholder:text-zinc-400"
                data-testid="subscribe-email-input"
              />
              <button
                type="submit"
                disabled={submitting}
                className="bg-zinc-900 text-white border-2 border-zinc-900 sm:border-l-0 px-6 py-3 text-sm font-bold uppercase tracking-widest hover:bg-red-600 hover:border-red-600 inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="subscribe-submit"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                Send me the printable
              </button>
            </form>
          )}
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-3">
            One email. No spam. Unsubscribe anytime.
          </div>
        </div>
      </div>
    </div>
  );
}
